import { useEffect, useRef, useState } from "react";
import { Paperclip } from "lucide-react";
import { theme, card, emptyFrame } from "../theme";
import { StatusTag, type StatusTone } from "../components/Drawing";
import { gbpMo, HARDENING_BENCHMARK_PENCE } from "../lib/pricing";
import { api, ApiError } from "../api/client";
import { ControlComments } from "../components/ControlComments";
import { useCurrentUser } from "../auth/useCurrentUser";
import { HardeningRecommendations } from "./HardeningRecommendations";
import { ControlTagsRow } from "../components/ControlTags";

import { cbPalette } from "../lib/colourBlind";
interface BenchmarkProgress {
  id: string;
  name: string;
  version: string;
  category: string;
  controlCount: number;
  statusCounts: Record<string, number>;
}

interface Control {
  id: string;
  refId: string;
  title: string;
  description: string;
  rationale: string | null;
  audit: string | null;
  remediation: string | null;
  level: string | null;
  profiles: string | null;
  assessment: string | null;
  section: string | null;
  status: string;
  notes: string | null;
  updatedAt: string | null;
  aiStatus: string | null;
  aiConfidence: string | null;
  aiFindings: { evidenceFound?: string[]; gaps?: string[]; recommendations?: string[]; observedValue?: string | null } | null;
  aiNote: string | null;
  aiAssessedAt: string | null;
  evidenceCount: number;
}

interface Evidence {
  id: string;
  fileName: string;
  contentType: string | null;
  sizeBytes: number;
  description: string | null;
  uploadedAt: string;
  controlId: string | null;
  control: { refId: string; title: string } | null;
}

interface AssessmentRun {
  id: string;
  benchmarkId: string;
  scope: string;
  controlIds: string[];
  status: string;
  progressJson: { total?: number; completed?: number; chunks?: number; currentChunk?: number } | null;
  overallScore: number | null;
  summary: string | null;
  costPence: number | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

const AI_STATUS_META: Record<string, { label: string; tone: StatusTone }> = {
  compliant:             { label: "AI: Compliant",       tone: "met" },
  non_compliant:         { label: "AI: Non-Compliant",   tone: "gap" },
  not_applicable:        { label: "AI: N/A",             tone: "none" },
  insufficient_evidence: { label: "AI: No Evidence",     tone: "none" },
};

const ACTIVE_RUN = new Set(["queued", "running", "cancelling"]);

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

const smallBtn = (variant: "primary" | "ghost" | "danger" = "ghost"): React.CSSProperties => ({
  fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 3, cursor: "pointer", fontFamily: theme.font,
  border: variant === "ghost" ? `1px solid ${theme.color.border}` : "none",
  background: variant === "primary" ? theme.color.accent : variant === "danger" ? theme.color.dangerBg : "transparent",
  color: variant === "primary" ? "#fff" : variant === "danger" ? theme.color.dangerText : theme.color.text,
});

const sectionLabel: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: theme.color.faint, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" };

const CATEGORY_LABELS: Record<string, string> = {
  cloud:            "Microsoft 365 & Azure",
  endpoint:         "Endpoint Protection",
  intune:           "Microsoft Intune",
  "windows-server": "Windows Server",
  "windows-desktop":"Windows Desktop",
};

const STATUS_OPTIONS: Array<{ id: string; label: string; color: string; bg: string; tone: StatusTone }> = cbPalette([
  { id: "not_started",     label: "Not Started",     color: theme.color.faint,       bg: theme.color.border,  tone: "none" },
  { id: "in_progress",     label: "In Progress",     color: "#b45309",               bg: "#451a03",           tone: "partial" },
  { id: "compliant",       label: "Compliant",       color: theme.color.successText,  bg: "#052e16",          tone: "met" },
  { id: "non_compliant",   label: "Non-Compliant",   color: theme.color.dangerText,   bg: "#450a0a",          tone: "gap" },
  { id: "not_applicable",  label: "Not Applicable",  color: theme.color.muted,        bg: theme.color.panelAlt, tone: "none" },
]);

function statusMeta(id: string) {
  return STATUS_OPTIONS.find((s) => s.id === id) ?? STATUS_OPTIONS[0];
}

function ProgressBar({ controlCount, statusCounts }: { controlCount: number; statusCounts: Record<string, number> }) {
  const compliant     = statusCounts.compliant ?? 0;
  const nonCompliant  = statusCounts.non_compliant ?? 0;
  const inProgress    = statusCounts.in_progress ?? 0;
  const notApplicable = statusCounts.not_applicable ?? 0;
  const assessed      = compliant + nonCompliant + inProgress + notApplicable;
  const pctCompliant  = controlCount > 0 ? Math.round((compliant / controlCount) * 100) : 0;
  const pctAssessed   = controlCount > 0 ? Math.round((assessed / controlCount) * 100) : 0;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: theme.color.subtle, marginBottom: 4 }}>
        <span>{compliant} / {controlCount} compliant ({pctCompliant}%)</span>
        <span>{pctAssessed}% assessed</span>
      </div>
      <div style={{ height: 6, borderRadius: 4, background: theme.color.panelAlt, overflow: "hidden", display: "flex" }}>
        <div style={{ width: `${pctCompliant}%`, background: theme.color.success, transition: "width 0.3s" }} />
        <div style={{ width: `${controlCount > 0 ? Math.round((nonCompliant / controlCount) * 100) : 0}%`, background: theme.color.danger, transition: "width 0.3s" }} />
        <div style={{ width: `${controlCount > 0 ? Math.round((inProgress / controlCount) * 100) : 0}%`, background: "#d97706", transition: "width 0.3s" }} />
      </div>
    </div>
  );
}

interface IntuneContext {
  connected: boolean;
  lastSync?: string | null;
  summary?: { total: number; compliant: number; noncompliant: number; unknown: number; other: number };
}

export function HardeningPage() {
  const { user } = useCurrentUser();
  const canSubscribe = !!user?.permissions?.includes("controls:write");
  const [benchmarks, setBenchmarks] = useState<BenchmarkProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBenchmark, setSelectedBenchmark] = useState<BenchmarkProgress | null>(null);
  const [controls, setControls] = useState<Control[]>([]);
  const [controlsLoading, setControlsLoading] = useState(false);
  const [expandedControl, setExpandedControl] = useState<string | null>(null);
  const [savingStatus, setSavingStatus] = useState<string | null>(null);
  const [intuneCtx, setIntuneCtx] = useState<IntuneContext | null>(null);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [runs, setRuns] = useState<AssessmentRun[]>([]);
  const [uploading, setUploading] = useState<string | null>(null); // "benchmark" | controlId
  const [evidenceNote, setEvidenceNote] = useState<Record<string, string>>({});
  const [assessError, setAssessError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [expandedRun, setExpandedRun] = useState(false);
  const pollRef = useRef<number | null>(null);

  const activeRun = runs.find((r) => ACTIVE_RUN.has(r.status)) ?? null;
  const lastRun = runs.find((r) => !ACTIVE_RUN.has(r.status)) ?? null;

  const loadControls = (benchmarkId: string) =>
    api.get<{ controls: Control[] }>(`/hardening/benchmarks/${benchmarkId}/controls`)
      .then((d) => setControls(d.controls))
      .catch(() => setControls([]));
  const loadEvidence = (benchmarkId: string) =>
    api.get<{ evidence: Evidence[] }>(`/hardening/benchmarks/${benchmarkId}/evidence`)
      .then((d) => setEvidence(d.evidence))
      .catch(() => setEvidence([]));
  const loadRuns = (benchmarkId: string) =>
    api.get<{ runs: AssessmentRun[] }>(`/hardening/benchmarks/${benchmarkId}/assessments`)
      .then((d) => setRuns(d.runs))
      .catch(() => setRuns([]));
  const loadProgress = () =>
    api.get<{ benchmarks: BenchmarkProgress[] }>("/hardening/progress")
      .then((d) => {
        setBenchmarks(d.benchmarks);
        setSelectedBenchmark((prev) => (prev ? d.benchmarks.find((b) => b.id === prev.id) ?? prev : prev));
      })
      .catch(() => {});

  // Poll an in-flight assessment every 3 s; refresh controls + progress when it finishes.
  useEffect(() => {
    if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
    if (!activeRun || !selectedBenchmark) return;
    const benchmarkId = selectedBenchmark.id;
    pollRef.current = window.setInterval(async () => {
      try {
        const { run } = await api.get<{ run: AssessmentRun }>(`/hardening/assessments/${activeRun.id}`);
        setRuns((prev) => prev.map((r) => (r.id === run.id ? run : r)));
        if (!ACTIVE_RUN.has(run.status)) {
          await Promise.all([loadControls(benchmarkId), loadProgress()]);
        }
      } catch { /* transient — keep polling */ }
    }, 3000);
    return () => { if (pollRef.current) window.clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRun?.id, activeRun?.status, selectedBenchmark?.id]);

  useEffect(() => {
    api.get<{ benchmarks: BenchmarkProgress[] }>("/hardening/progress")
      .then((d) => setBenchmarks(d.benchmarks))
      .catch(() => setBenchmarks([]))
      .finally(() => setLoading(false));
    api.get<IntuneContext>("/hardening/intune-context")
      .then((d) => setIntuneCtx(d))
      .catch(() => {});
  }, []);

  const openBenchmark = (b: BenchmarkProgress) => {
    setSelectedBenchmark(b);
    setExpandedControl(null);
    setAssessError(null);
    setExpandedRun(false);
    setControlsLoading(true);
    Promise.all([loadControls(b.id), loadEvidence(b.id), loadRuns(b.id)]).finally(() => setControlsLoading(false));
  };
  const openBenchmarkById = (id: string) => { const b = benchmarks.find((x) => x.id === id); if (b) openBenchmark(b); };

  const uploadEvidence = async (file: File, controlId: string | null) => {
    if (!selectedBenchmark) return;
    const key = controlId ?? "benchmark";
    setUploading(key);
    setAssessError(null);
    try {
      const contentType = file.type || "application/octet-stream";
      const { blobPath, uploadUrl } = await api.post<{ blobPath: string; uploadUrl: string }>("/hardening/evidence/request-upload", {
        benchmarkId: selectedBenchmark.id, controlId, fileName: file.name, contentType,
      });
      const putRes = await fetch(uploadUrl, { method: "PUT", headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": contentType }, body: file });
      if (!putRes.ok) throw new Error("Upload to storage failed");
      await api.post("/hardening/evidence", {
        benchmarkId: selectedBenchmark.id, controlId, blobPath, fileName: file.name, contentType, sizeBytes: file.size,
        description: evidenceNote[key]?.trim() || null,
      });
      setEvidenceNote((prev) => ({ ...prev, [key]: "" }));
      await Promise.all([loadEvidence(selectedBenchmark.id), loadControls(selectedBenchmark.id)]);
    } catch (e) {
      setAssessError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const deleteEvidence = async (id: string) => {
    if (!selectedBenchmark) return;
    if (!window.confirm("Delete this evidence file?")) return;
    try {
      await api.del(`/hardening/evidence/${id}`);
      await Promise.all([loadEvidence(selectedBenchmark.id), loadControls(selectedBenchmark.id)]);
    } catch (e) {
      setAssessError(e instanceof Error ? e.message : "Delete failed");
    }
  };

  const downloadEvidence = async (id: string) => {
    try {
      const { url } = await api.get<{ url: string }>(`/hardening/evidence/${id}/download-url`);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      setAssessError(e instanceof Error ? e.message : "Download failed");
    }
  };

  const startAssessment = async (scope: "evidenced" | "all" | "selected", controlIds?: string[]) => {
    if (!selectedBenchmark) return;
    setStarting(true);
    setAssessError(null);
    try {
      const { run } = await api.post<{ run: AssessmentRun }>(`/hardening/benchmarks/${selectedBenchmark.id}/assess`, { scope, controlIds });
      setRuns((prev) => [run, ...prev]);
      setExpandedRun(true);
    } catch (e) {
      if (e instanceof ApiError && e.status === 402) setAssessError(e.message || "AI spend cap or token budget reached.");
      else if (e instanceof ApiError && e.status === 409) setAssessError("An assessment for this benchmark is already running.");
      else setAssessError(e instanceof Error ? e.message : "Could not start assessment");
    } finally {
      setStarting(false);
    }
  };

  const cancelAssessment = async () => {
    if (!activeRun) return;
    try {
      await api.post(`/hardening/assessments/${activeRun.id}/cancel`);
      setRuns((prev) => prev.map((r) => (r.id === activeRun.id ? { ...r, status: r.status === "queued" ? "cancelled" : "cancelling" } : r)));
    } catch (e) {
      setAssessError(e instanceof Error ? e.message : "Cancel failed");
    }
  };

  const updateStatus = async (controlId: string, status: string) => {
    setSavingStatus(controlId);
    try {
      const notes = controls.find((c) => c.id === controlId)?.notes ?? "";
      await api.patch(`/hardening/controls/${controlId}/status`, { status, notes: notes || null });
      setControls((prev) => prev.map((c) => c.id === controlId ? { ...c, status, aiNote: null } : c));
      if (selectedBenchmark) {
        setBenchmarks((prev) => prev.map((b) => {
          if (b.id !== selectedBenchmark.id) return b;
          const oldStatus = controls.find((c) => c.id === controlId)?.status ?? "not_started";
          const counts = { ...b.statusCounts };
          if (oldStatus !== "not_started") counts[oldStatus] = Math.max(0, (counts[oldStatus] ?? 0) - 1);
          if (status !== "not_started") counts[status] = (counts[status] ?? 0) + 1;
          return { ...b, statusCounts: counts };
        }));
      }
    } finally {
      setSavingStatus(null);
    }
  };

  // Mirror the newest note into the control's notes field for AI assessment context.
  const mirrorNote = async (controlId: string, body: string) => {
    try {
      const status = controls.find((c) => c.id === controlId)?.status ?? "not_started";
      await api.patch(`/hardening/controls/${controlId}/status`, { status, notes: body });
      setControls((prev) => prev.map((c) => c.id === controlId ? { ...c, notes: body } : c));
    } catch { /* the note itself is saved; the mirror is best-effort */ }
  };

  const grouped = benchmarks.reduce<Record<string, BenchmarkProgress[]>>((acc, b) => {
    (acc[b.category] ??= []).push(b);
    return acc;
  }, {});

  if (loading) {
    return (
      <div style={{ padding: "40px 32px" }}>
        <div style={{ fontSize: 11, color: theme.color.faint }}>Loading…</div>
      </div>
    );
  }

  if (benchmarks.length === 0) {
    return (
      <div style={{ padding: "40px 32px", maxWidth: 680 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: theme.color.heading, marginBottom: 8 }}>
          CIS Hardening Benchmarks
        </div>
        <div style={{ fontSize: 14, color: theme.color.subtle, lineHeight: 1.6 }}>
          No benchmarks are active for your organisation. Subscribe to one or more CIS Microsoft benchmarks from My Services ({gbpMo(HARDENING_BENCHMARK_PENCE)} each, Self-Service + AI tier and above).
        </div>
        <div style={{ marginTop: 20 }}>
          <HardeningRecommendations canSubscribe={canSubscribe} onSubscribed={loadProgress} />
        </div>
      </div>
    );
  }

  // Controls view
  if (selectedBenchmark) {
    const sections = controls.reduce<Record<string, Control[]>>((acc, c) => {
      const key = c.section ?? "General";
      (acc[key] ??= []).push(c);
      return acc;
    }, {});

    return (
      <div style={{ padding: "28px 32px" }}>
        {/* Back link */}
        <button
          onClick={() => setSelectedBenchmark(null)}
          style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.accentText, fontSize: 13, fontWeight: 600, marginBottom: 20, padding: 0, display: "flex", alignItems: "center", gap: 6 }}
        >
          ← Back to Benchmarks
        </button>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: theme.color.heading }}>{selectedBenchmark.name}</div>
          <div style={{ fontSize: 12, color: theme.color.faint, marginTop: 2 }}>{selectedBenchmark.version}</div>
          <div style={{ marginTop: 12, maxWidth: 600 }}>
            <ProgressBar controlCount={selectedBenchmark.controlCount} statusCounts={selectedBenchmark.statusCounts} />
          </div>
        </div>

        {/* Intune context banner — shown for intune-category benchmarks when connected */}
        {selectedBenchmark.category === "intune" && intuneCtx?.connected && intuneCtx.summary && (
          <div style={{ ...card, marginBottom: 16, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", background: theme.color.panelAlt }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: theme.color.faint, flexShrink: 0 }}>
              Intune Devices
            </div>
            {[
              { label: "Total",         value: intuneCtx.summary.total,        color: theme.color.text },
              { label: "Compliant",     value: intuneCtx.summary.compliant,    color: theme.color.successText },
              { label: "Non-Compliant", value: intuneCtx.summary.noncompliant, color: theme.color.dangerText },
              { label: "Unknown",       value: intuneCtx.summary.unknown,      color: theme.color.faint },
            ].map((stat) => (
              <div key={stat.label} style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: stat.color }}>{stat.value}</span>
                <span style={{ fontSize: 11, color: theme.color.faint }}>{stat.label}</span>
              </div>
            ))}
            {intuneCtx.lastSync && (
              <div style={{ fontSize: 11, color: theme.color.faint, marginLeft: "auto" }}>
                Last sync {new Date(intuneCtx.lastSync).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </div>
            )}
          </div>
        )}

        {/* AI assessment panel */}
        {(() => {
          const benchmarkWide = evidence.filter((e) => !e.controlId);
          const evidencedControls = controls.filter((c) => c.evidenceCount > 0).length;
          const progress = activeRun?.progressJson;
          const scopeLabel = (r: AssessmentRun) => r.scope === "all" ? "all controls" : r.scope === "selected" ? `${r.controlIds.length} selected control${r.controlIds.length === 1 ? "" : "s"}` : "controls with evidence";
          return (
            <div style={{ ...card, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>AI evidence assessment</div>
                  <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2, lineHeight: 1.5 }}>
                    Upload GPO exports (<code>gpresult /h</code>, Backup-GPO XML), Intune policy JSON, registry / PowerShell output or screenshots, then have the AI check each control&apos;s configured value against the CIS recommendation.
                    {evidence.length > 0 && <> {evidence.length} file{evidence.length === 1 ? "" : "s"} uploaded ({benchmarkWide.length} benchmark-wide, {evidencedControls} control{evidencedControls === 1 ? "" : "s"} with direct evidence).</>}
                  </div>
                </div>
                {activeRun ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontSize: 12, color: theme.color.accentText, fontWeight: 600 }}>
                      {activeRun.status === "queued" ? "Queued…" : activeRun.status === "cancelling" ? "Cancelling…" : progress?.total
                        ? `Assessing… ${progress.completed ?? 0} / ${progress.total} controls${(progress.chunks ?? 1) > 1 ? ` (batch ${progress.currentChunk} of ${progress.chunks})` : ""}`
                        : "Assessing…"}
                    </div>
                    {activeRun.status !== "cancelling" && <button onClick={cancelAssessment} style={smallBtn("ghost")}>Cancel</button>}
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      onClick={() => startAssessment("evidenced")}
                      disabled={starting || evidence.length === 0}
                      title={evidence.length === 0 ? "Upload evidence first" : benchmarkWide.length > 0 ? "Benchmark-wide evidence present — assesses every control" : "Assesses only controls that have evidence attached"}
                      style={{ ...smallBtn("primary"), opacity: starting || evidence.length === 0 ? 0.5 : 1 }}
                    >
                      {starting ? "Starting…" : "Assess with AI"}
                    </button>
                    {benchmarkWide.length === 0 && (
                      <button onClick={() => startAssessment("all")} disabled={starting || evidence.length === 0} style={{ ...smallBtn("ghost"), opacity: starting || evidence.length === 0 ? 0.5 : 1 }} title="Assess every control — those without evidence will come back as No Evidence">
                        Assess all controls
                      </button>
                    )}
                  </div>
                )}
              </div>

              {assessError && (
                <div style={{ marginTop: 10, fontSize: 12, color: theme.color.dangerText, background: theme.color.dangerBg, padding: "8px 10px", borderRadius: 3 }}>{assessError}</div>
              )}

              {/* Last completed run */}
              {lastRun && !activeRun && (
                <div style={{ marginTop: 12, borderTop: `1px solid ${theme.color.border}`, paddingTop: 10 }}>
                  <div onClick={() => setExpandedRun((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 12 }}>
                    <span style={{ fontWeight: 700, color: lastRun.status === "done" ? theme.color.heading : theme.color.dangerText }}>
                      {lastRun.status === "done" ? `Last assessment: ${lastRun.overallScore ?? 0}% of assessed controls compliant` : lastRun.status === "cancelled" ? "Last assessment cancelled" : "Last assessment failed"}
                    </span>
                    <span style={{ color: theme.color.faint }}>{formatDate(lastRun.createdAt)} · {scopeLabel(lastRun)}{lastRun.costPence != null ? ` · £${(lastRun.costPence / 100).toFixed(2)}` : ""}</span>
                    <span style={{ marginLeft: "auto", color: theme.color.faint, fontSize: 10 }}>{expandedRun ? "▲" : "▼"}</span>
                  </div>
                  {expandedRun && (
                    <div style={{ marginTop: 8, fontSize: 12, color: theme.color.subtle, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                      {lastRun.status === "done" ? (lastRun.summary || "No summary returned.") : (lastRun.errorMessage || "No details.")}
                    </div>
                  )}
                </div>
              )}

              {/* Benchmark-wide evidence */}
              <div style={{ marginTop: 12, borderTop: `1px solid ${theme.color.border}`, paddingTop: 10 }}>
                <div style={sectionLabel}>Benchmark-wide evidence</div>
                {benchmarkWide.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
                    {benchmarkWide.map((e) => (
                      <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                        <span style={{ color: theme.color.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.fileName}</span>
                        <span style={{ color: theme.color.faint }}>{formatBytes(e.sizeBytes)} · {formatDate(e.uploadedAt)}</span>
                        {e.description && <span style={{ color: theme.color.subtle, fontStyle: "italic" }}>— {e.description}</span>}
                        <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                          <button onClick={() => downloadEvidence(e.id)} style={{ ...smallBtn("ghost"), padding: "2px 8px", fontSize: 11 }}>Open</button>
                          <button onClick={() => deleteEvidence(e.id)} style={{ ...smallBtn("danger"), padding: "2px 8px", fontSize: 11 }}>Delete</button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    type="text"
                    placeholder="Note (optional) — e.g. gpresult /h from DC01"
                    value={evidenceNote.benchmark ?? ""}
                    onChange={(ev) => setEvidenceNote((prev) => ({ ...prev, benchmark: ev.target.value }))}
                    style={{ flex: 1, minWidth: 200, fontSize: 12, padding: "6px 10px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panelAlt, color: theme.color.text, fontFamily: theme.font, outline: "none" }}
                  />
                  <label style={{ ...smallBtn("ghost"), opacity: uploading ? 0.5 : 1 }}>
                    {uploading === "benchmark" ? "Uploading…" : "Upload file"}
                    <input type="file" disabled={!!uploading} style={{ display: "none" }} onChange={(ev) => { const f = ev.target.files?.[0]; if (f) uploadEvidence(f, null); ev.target.value = ""; }} />
                  </label>
                </div>
                <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 6 }}>
                  Accepted: HTML, XML, JSON, TXT, CSV, LOG, REG, INF, PS1, PDF, PNG, JPEG, GIF, WEBP — up to 25 MB. Evidence uploaded here is available to every control; per-control evidence can be attached below.
                </div>
              </div>
            </div>
          );
        })()}

        {/* Status legend */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 20 }}>
          {STATUS_OPTIONS.map((s) => (
            <StatusTag key={s.id} tone={s.tone}>{s.label}</StatusTag>
          ))}
        </div>

        {controlsLoading ? (
          <div style={{ fontSize: 12, color: theme.color.faint }}>Loading controls…</div>
        ) : controls.length === 0 ? (
          <div style={{ ...emptyFrame, color: theme.color.subtle, fontSize: 13 }}>
            No controls have been loaded for this benchmark yet. Controls are populated from CIS benchmark markdown files.
          </div>
        ) : (
          Object.entries(sections).map(([section, sectionControls]) => (
            <div key={section} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: theme.color.faint, marginBottom: 8 }}>
                {section}
              </div>
              {sectionControls.map((ctrl) => {
                const meta = statusMeta(ctrl.status);
                const isExpanded = expandedControl === ctrl.id;
                return (
                  <div key={ctrl.id} style={{ ...card, marginBottom: 8, padding: 0, overflow: "hidden" }}>
                    {/* Header row */}
                    <div
                      onClick={() => setExpandedControl(isExpanded ? null : ctrl.id)}
                      style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer" }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, minWidth: 40, flexShrink: 0 }}>
                        {ctrl.refId}
                      </div>
                      <div style={{ flex: 1, fontSize: 13, fontWeight: 500, color: theme.color.text, lineHeight: 1.4 }}>
                        {ctrl.title}
                      </div>
                      {ctrl.evidenceCount > 0 && (
                        <div title={`${ctrl.evidenceCount} evidence file${ctrl.evidenceCount === 1 ? "" : "s"} attached`} style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3, background: theme.color.accentBg, color: theme.color.accentText, flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 3 }}>
                          <Paperclip size={10} />{ctrl.evidenceCount}
                        </div>
                      )}
                      {ctrl.aiStatus && AI_STATUS_META[ctrl.aiStatus] && (
                        <StatusTag tone={AI_STATUS_META[ctrl.aiStatus].tone}>{AI_STATUS_META[ctrl.aiStatus].label}</StatusTag>
                      )}
                      {ctrl.level && (
                        <div style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3, background: theme.color.panelAlt, color: theme.color.faint, flexShrink: 0 }}>
                          {ctrl.level}
                        </div>
                      )}
                      <StatusTag tone={meta.tone}>{meta.label}</StatusTag>
                      <div style={{ color: theme.color.faint, fontSize: 10 }}>{isExpanded ? "▲" : "▼"}</div>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div style={{ borderTop: `1px solid ${theme.color.border}`, padding: "16px" }}>
                        {/* Keyed like the notes thread so a benchmark control never collides with a standard's control id. */}
                        <ControlTagsRow controlId={`hardening:${ctrl.id}`} />
                        <div style={{ fontSize: 13, color: theme.color.text, lineHeight: 1.6, marginBottom: 12 }}>{ctrl.description}</div>

                        {ctrl.rationale && (
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Rationale</div>
                            <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>{ctrl.rationale}</div>
                          </div>
                        )}

                        {ctrl.audit && (
                          <div style={{ marginBottom: 12 }}>
                            <div style={sectionLabel}>Audit procedure{ctrl.assessment ? ` · ${ctrl.assessment}` : ""}{ctrl.profiles ? ` · ${ctrl.profiles}` : ""}</div>
                            <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6, fontFamily: "monospace", background: theme.color.panelAlt, padding: "10px 12px", borderRadius: 3, whiteSpace: "pre-wrap" }}>{ctrl.audit}</div>
                          </div>
                        )}

                        {ctrl.remediation && (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Remediation</div>
                            <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6, fontFamily: "monospace", background: theme.color.panelAlt, padding: "10px 12px", borderRadius: 3, whiteSpace: "pre-wrap" }}>{ctrl.remediation}</div>
                          </div>
                        )}

                        {/* AI verdict */}
                        {ctrl.aiStatus && AI_STATUS_META[ctrl.aiStatus] && (
                          <div style={{ marginBottom: 16, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "12px 14px", background: theme.color.panelAlt }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                              <StatusTag tone={AI_STATUS_META[ctrl.aiStatus].tone}>{AI_STATUS_META[ctrl.aiStatus].label}</StatusTag>
                              {ctrl.aiConfidence && <span style={{ fontSize: 11, color: theme.color.faint }}>{ctrl.aiConfidence} confidence</span>}
                              {ctrl.aiAssessedAt && <span style={{ fontSize: 11, color: theme.color.faint }}>· {formatDate(ctrl.aiAssessedAt)}</span>}
                              {ctrl.aiStatus === "compliant" && ctrl.status !== "compliant" && (
                                <button onClick={() => updateStatus(ctrl.id, "compliant")} disabled={savingStatus === ctrl.id} style={{ ...smallBtn("primary"), marginLeft: "auto", padding: "3px 10px", fontSize: 11 }}>
                                  Confirm compliant
                                </button>
                              )}
                            </div>
                            {ctrl.aiNote && <div style={{ fontSize: 12, color: theme.color.warningText, marginBottom: 8 }}>{ctrl.aiNote}</div>}
                            {ctrl.aiFindings?.observedValue && (
                              <div style={{ fontSize: 12, color: theme.color.text, marginBottom: 6 }}><strong>Observed:</strong> <code style={{ fontSize: 11 }}>{ctrl.aiFindings.observedValue}</code></div>
                            )}
                            {(["evidenceFound", "gaps", "recommendations"] as const).map((key) => {
                              const items = ctrl.aiFindings?.[key] ?? [];
                              if (items.length === 0) return null;
                              const label = key === "evidenceFound" ? "Evidence found" : key === "gaps" ? "Gaps" : "Recommendations";
                              return (
                                <div key={key} style={{ marginTop: 6 }}>
                                  <div style={sectionLabel}>{label}</div>
                                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>
                                    {items.map((it, n) => <li key={n}>{it}</li>)}
                                  </ul>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Per-control evidence */}
                        {(() => {
                          const mine = evidence.filter((e) => e.controlId === ctrl.id);
                          return (
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <div style={{ ...sectionLabel, marginBottom: 0 }}>Evidence for this control</div>
                                {!activeRun && (
                                  <button
                                    onClick={() => startAssessment("selected", [ctrl.id])}
                                    disabled={starting || (mine.length === 0 && evidence.filter((e) => !e.controlId).length === 0)}
                                    title="Run the AI assessment for just this control"
                                    style={{ ...smallBtn("ghost"), marginLeft: "auto", padding: "3px 10px", fontSize: 11, opacity: starting || (mine.length === 0 && evidence.filter((e) => !e.controlId).length === 0) ? 0.5 : 1 }}
                                  >
                                    Assess this control
                                  </button>
                                )}
                              </div>
                              {mine.length > 0 && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
                                  {mine.map((e) => (
                                    <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                                      <span style={{ color: theme.color.text, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.fileName}</span>
                                      <span style={{ color: theme.color.faint }}>{formatBytes(e.sizeBytes)} · {formatDate(e.uploadedAt)}</span>
                                      {e.description && <span style={{ color: theme.color.subtle, fontStyle: "italic" }}>— {e.description}</span>}
                                      <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                                        <button onClick={() => downloadEvidence(e.id)} style={{ ...smallBtn("ghost"), padding: "2px 8px", fontSize: 11 }}>Open</button>
                                        <button onClick={() => deleteEvidence(e.id)} style={{ ...smallBtn("danger"), padding: "2px 8px", fontSize: 11 }}>Delete</button>
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                <input
                                  type="text"
                                  placeholder="Note (optional) — e.g. screenshot of Settings Catalog value"
                                  value={evidenceNote[ctrl.id] ?? ""}
                                  onChange={(ev) => setEvidenceNote((prev) => ({ ...prev, [ctrl.id]: ev.target.value }))}
                                  style={{ flex: 1, minWidth: 200, fontSize: 12, padding: "6px 10px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panelAlt, color: theme.color.text, fontFamily: theme.font, outline: "none" }}
                                />
                                <label style={{ ...smallBtn("ghost"), opacity: uploading ? 0.5 : 1 }}>
                                  {uploading === ctrl.id ? "Uploading…" : "Attach file"}
                                  <input type="file" disabled={!!uploading} style={{ display: "none" }} onChange={(ev) => { const f = ev.target.files?.[0]; if (f) uploadEvidence(f, ctrl.id); ev.target.value = ""; }} />
                                </label>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Status controls */}
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>Status</div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {STATUS_OPTIONS.map((s) => (
                              <button
                                key={s.id}
                                disabled={savingStatus === ctrl.id}
                                onClick={() => updateStatus(ctrl.id, s.id)}
                                style={{
                                  fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 3, cursor: "pointer",
                                  border: ctrl.status === s.id ? "none" : `1px solid ${theme.color.border}`,
                                  background: ctrl.status === s.id ? s.bg : "transparent",
                                  color: ctrl.status === s.id ? s.color : theme.color.faint,
                                  textTransform: "uppercase", letterSpacing: "0.04em",
                                  opacity: savingStatus === ctrl.id ? 0.5 : 1,
                                  transition: "background 0.15s, color 0.15s",
                                }}
                              >
                                {s.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Notes */}
                        <ControlComments
                          controlId={`hardening:${ctrl.id}`}
                          open={true}
                          label="Notes"
                          placeholder="Add implementation notes…"
                          legacyNote={ctrl.notes}
                          onPost={(body) => mirrorNote(ctrl.id, body)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    );
  }

  // Benchmark overview
  return (
    <div style={{ padding: "28px 32px" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: theme.color.heading }}>CIS Hardening Benchmarks</div>
        <div style={{ fontSize: 13, color: theme.color.subtle, marginTop: 4 }}>
          Track your organisation's progress against CIS Microsoft security benchmarks.
        </div>
      </div>

      <HardeningRecommendations canSubscribe={canSubscribe} onSubscribed={loadProgress} onOpen={openBenchmarkById} />

      {Object.entries(grouped).map(([category, categoryBenchmarks]) => (
        <div key={category} style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.color.faint, marginBottom: 10 }}>
            {CATEGORY_LABELS[category] ?? category}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {categoryBenchmarks.map((b) => (
              <div
                key={b.id}
                onClick={() => openBenchmark(b)}
                style={{ ...card, cursor: "pointer", transition: "border-color 0.15s", userSelect: "none" }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = theme.color.accent)}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--c-line)")}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, marginBottom: 2 }}>{b.name}</div>
                <div style={{ fontSize: 11, color: theme.color.faint, marginBottom: 10 }}>{b.version}</div>
                {b.controlCount > 0 ? (
                  <ProgressBar controlCount={b.controlCount} statusCounts={b.statusCounts} />
                ) : (
                  <div style={{ fontSize: 11, color: theme.color.faint }}>Controls not yet loaded</div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
